import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Activity } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleString(lang === "zh" ? "zh-CN" : "en-US") : "—");

const statusClass = (code) => {
  if (!code) return "bg-secondary text-muted-foreground";
  if (code < 300) return "bg-emerald-100 text-emerald-800";
  if (code < 400) return "bg-sky-100 text-sky-800";
  if (code < 500) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-800";
};

const methodClass = (m) => {
  const map = {
    GET: "bg-sky-50 text-sky-700 border-sky-200",
    POST: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PUT: "bg-amber-50 text-amber-700 border-amber-200",
    PATCH: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-red-50 text-red-700 border-red-200",
  };
  return map[m] || "bg-secondary text-muted-foreground";
};

export const ApiTokenLogsPanel = () => {
  const { t, lang } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [filterTokenId, setFilterTokenId] = useState("all");
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterTokenId && filterTokenId !== "all" ? { token_id: filterTokenId } : {};
      const { data } = await api.get("/auth/api-tokens/logs", { params });
      setLogs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filterTokenId]);

  const fetchTokens = async () => {
    try {
      const { data } = await api.get("/auth/api-tokens");
      setTokens(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { fetchTokens(); }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight text-foreground flex items-center gap-2">
            <Activity size={18} /> {t("apiTokens.logs")}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">{t("apiTokens.logsSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterTokenId} onValueChange={setFilterTokenId}>
            <SelectTrigger className="w-[220px]" data-testid="log-filter-token-select">
              <SelectValue placeholder={t("apiTokens.filterAllTokens")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("apiTokens.filterAllTokens")}</SelectItem>
              {tokens.map((tk) => (
                <SelectItem key={tk.id} value={tk.id}>{tk.name} <span className="text-muted-foreground ml-1">{tk.prefix}…</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchLogs} disabled={loading} data-testid="refresh-api-token-logs" className="rounded-md">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("common.refresh")}
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="api-token-logs-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("apiTokens.logs_time")}</TableHead>
              <TableHead>{t("apiTokens.logs_token")}</TableHead>
              <TableHead>{t("apiTokens.logs_method")}</TableHead>
              <TableHead>{t("apiTokens.logs_path")}</TableHead>
              <TableHead>{t("apiTokens.logs_status")}</TableHead>
              <TableHead className="text-right">{t("apiTokens.logs_duration")}</TableHead>
              <TableHead>{t("apiTokens.logs_ip")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                  {t("apiTokens.logsEmpty")}
                </TableCell>
              </TableRow>
            )}
            {logs.map((lg) => (
              <TableRow key={lg.id} data-testid={`api-token-log-row-${lg.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtDate(lg.timestamp, lang)}</TableCell>
                <TableCell className="text-xs font-medium">{lg.token_name || "—"}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono font-bold ${methodClass(lg.method)}`}>
                    {lg.method}
                  </span>
                </TableCell>
                <TableCell><code className="font-mono text-xs">{lg.path}</code></TableCell>
                <TableCell>
                  <Badge className={`${statusClass(lg.status_code)} border-transparent hover:${statusClass(lg.status_code)}`}>{lg.status_code}</Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{lg.duration_ms} ms</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{lg.ip || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-muted-foreground">{t("apiTokens.logsRetention")}</p>
    </div>
  );
};
